import * as React from 'react';
import { getErrorMessage, validateRowsWithData } from '../helpers';
import { SpreadsheetV2Props } from '../SpreadsheetV2';
import { HotTableType, SpreadsheetCallbackType } from '../types';
import { useSliderState } from './useSliderState';
import { ERRORS_BATCH_UPDATE_EVENT_COMPLETED } from '../constants';

/**
 * Creates an object with methods that interact with Handsontable
 * @param ref Ref to the HotTable component
 * @param columns Array of configuration objects coming from props
 * @param sliderState Return value of useSliderState
 * @param onLoad Callback that is called when the spreadsheet is loaded
 */
export const useSpreadsheetCallbacks = (
  ref: React.MutableRefObject<HotTableType | null>,
  columns: SpreadsheetV2Props['columns'],
  sliderState: ReturnType<typeof useSliderState>,
  onLoad: SpreadsheetV2Props['onLoad'],
): SpreadsheetCallbackType | null => {
  const [callbacks, setCallbacks] = React.useState<SpreadsheetCallbackType | null>(null);
  const [_, setUpdateVersion] = React.useState(0);
  const hotInstance = ref.current?.hotInstance;

  React.useEffect(() => {
    if (callbacks || !hotInstance) {
      return;
    }

    const callbackValue = {
      getDataAtCell: hotInstance.getDataAtCell.bind(hotInstance),
      getSelected: hotInstance.getSelected.bind(hotInstance),
      selectCell: hotInstance.selectCell.bind(hotInstance),
      setDataAtCell: hotInstance.setDataAtCell.bind(hotInstance),
      getSelectedColumn: function getSelectedColumn() {
        const selectedRange = hotInstance.getSelected.bind(hotInstance)();
        const selectedColumn = selectedRange && selectedRange[0][1];
        return columns[selectedColumn];
      },
      validate: function validate(data) {
        return validateRowsWithData(data, hotInstance);
      },
      getAllErrors: function getAllErrors() {
        const invalidCells = hotInstance.getCellsMeta().filter(cell => cell.valid === false);
        const errors = invalidCells.map(invalidCell => ({
          col: invalidCell.col,
          row: invalidCell.row,
          errorMessage: getErrorMessage(columns, invalidCell.col),
          externalErrorMessage: invalidCell.externalErrorMessage,
          hasExternalError: invalidCell.hasExternalError,
        }));
        return errors;
      },
      /*
        Setting these attributes explicitly instead of spreading ...sliderState because
        since `onLoad` is most likely going to be a useState setter, the values of "open"
        and "activeTab" won't change. If you need to know whether the slider is open or
        closed _right now_ then you need to use onSliderToggle.
      */
      toggleOpen: sliderState.toggleOpen,
      openSlider: sliderState.openSlider,
      closeSlider: sliderState.closeSlider,
      switchTab: sliderState.switchTab,
    };

    setCallbacks(callbackValue);
    if (onLoad) {
      onLoad(callbackValue);
    }
  }, [onLoad, hotInstance, columns, sliderState, callbacks]);

  React.useEffect(() => {
    window.addEventListener(ERRORS_BATCH_UPDATE_EVENT_COMPLETED, () => setUpdateVersion(prev => prev + 1));
    return () => {
      window.removeEventListener(ERRORS_BATCH_UPDATE_EVENT_COMPLETED, () => setUpdateVersion(prev => prev + 1));
    };
  }, []);
  return callbacks;
};
